import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import {
  DEATH_MANNERS,
  eatDayOf,
  formatIsoDay,
  type DeathCauseRole,
  type DeathManner,
} from '@wodi/domain';
import { DIAGNOSIS_CODE_SYSTEM_KEYS, type TerminologyCoding } from '@wodi/shared';
import {
  Button,
  DateField,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Section,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { can, sessionQuery } from '../api/session.ts';
import { deathFormOptionsQuery, deathVisitFormQuery, recordDeath } from '../api/vital-events.ts';
import { visitQuery } from '../api/visits.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { TerminologyPicker } from '../terminology/terminology-picker.tsx';
import { toasts } from '../toasts.ts';
import styles from './vital-events.module.css';
import {
  CAUSE_ROLE_WORDS,
  eatClockNow,
  eatInstant,
  errorText,
  MANNER_WORDS,
  PLACE_WORDS,
  validTime,
} from './words.ts';

// Record a death from the patient's open stay or casualty visit
// (docs/plans/phase2-mortuary-and-notifications.md §6). Causes are ICD-11 codes from the
// configured terminology. Confirming marks the patient deceased everywhere and, for a stay,
// discharges it as died and frees the bed. For review by the clinical safety lead.

const route = getRouteApi('/_app/visits/$visitId/record-death');

interface CauseDraft {
  key: string;
  role: DeathCauseRole;
  coding: TerminologyCoding | null;
  words: string;
  onset: string;
}

function emptyCause(role: DeathCauseRole): CauseDraft {
  return { key: crypto.randomUUID(), role, coding: null, words: '', onset: '' };
}

function CauseFields({
  cause,
  onChange,
  onRemove,
}: {
  cause: CauseDraft;
  onChange: (next: CauseDraft) => void;
  onRemove?: () => void;
}) {
  const title = CAUSE_ROLE_WORDS[cause.role];
  return (
    <fieldset className={styles.cause} aria-label={title}>
      <legend className={styles.causeTitle}>{title}</legend>
      {cause.coding === null ? (
        <TerminologyPicker
          label={`${title} (ICD-11)`}
          systems={DIAGNOSIS_CODE_SYSTEM_KEYS}
          onSelect={(coding) => {
            onChange({ ...cause, coding });
          }}
        />
      ) : (
        <p className={styles.prose}>
          <span className={`wodi-id ${styles.code}`}>{cause.coding.code}</span>
          {cause.coding.display}{' '}
          <Button
            dense
            variant="quiet"
            onPress={() => {
              onChange({ ...cause, coding: null });
            }}
          >
            Change code
          </Button>
        </p>
      )}
      <div className={styles.row}>
        <TextField
          label="Your words (optional)"
          description="Only on its own while ICD-11 isn't set up."
          value={cause.words}
          onChange={(words) => {
            onChange({ ...cause, words });
          }}
        />
        <TextField
          label="Time from onset (optional)"
          description="Like 2 days or 3 hours."
          value={cause.onset}
          onChange={(onset) => {
            onChange({ ...cause, onset });
          }}
        />
      </div>
      {onRemove === undefined ? null : (
        <div className={styles.actions}>
          <Button dense variant="quiet" onPress={onRemove}>
            Remove this condition
          </Button>
        </div>
      )}
    </fieldset>
  );
}

function filled(c: CauseDraft): boolean {
  return c.coding !== null || c.words.trim() !== '';
}

export function RecordDeathPage() {
  const { visitId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const visit = useQuery(visitQuery(visitId));
  const options = useQuery(deathFormOptionsQuery);
  const form = useQuery(deathVisitFormQuery(visitId));
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [day, setDay] = useState<string | null>(formatIsoDay(eatDayOf(new Date())));
  const [time, setTime] = useState(() => eatClockNow());
  const [chosenPlace, setPlace] = useState<'casualty' | 'brought_in_dead'>('casualty');
  const [certifier, setCertifier] = useState<string | null>(session?.user.id ?? null);
  const [manner, setManner] = useState<DeathManner | null>(null);
  const [immediate, setImmediate] = useState(() => emptyCause('immediate'));
  const [underlying, setUnderlying] = useState(() => emptyCause('underlying'));
  const [contributing, setContributing] = useState<CauseDraft[]>([]);
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);
  const mayRecord = session !== null && can(session, 'deaths.record');

  const causes = [immediate, underlying, ...contributing].filter(filled).map((c) => ({
    role: c.role,
    coding: c.coding,
    clinicianText: c.words.trim() === '' ? null : c.words.trim(),
    onsetInterval: c.onset.trim() === '' ? null : c.onset.trim(),
  }));
  const record = useMutation({
    mutationFn: () => {
      const type = visit.data?.type;
      return recordDeath(visitId, {
        clientRequestId,
        diedAt: eatInstant(day ?? '', time),
        place: type === 'inpatient' ? 'ward' : chosenPlace,
        certifiedBy: certifier ?? '',
        manner: manner ?? 'pending_investigation',
        causes,
        notes: notes.trim() === '' ? null : notes.trim(),
      });
    },
    onSuccess: async (death) => {
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: ['patients'] });
      await queryClient.invalidateQueries({ queryKey: ['inpatient'] });
      await queryClient.invalidateQueries({ queryKey: ['deaths'] });
      toasts.show({ title: 'Death recorded' });
      await navigate({ to: '/deaths/$deathId', params: { deathId: death.id } });
    },
  });

  if (visit.isPending || form.isPending) return <LoadingBlock label="Loading the visit" />;
  if (visit.isError || form.isError) {
    return (
      <Notice
        tone="error"
        action={
          <Button
            onPress={() => {
              void visit.refetch();
              void form.refetch();
            }}
          >
            Try again
          </Button>
        }
      >
        {errorText(visit.error ?? form.error, "The visit couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const v = visit.data;
  // The open stay or casualty visit, or the stay a death entered in error ended (plan D14).
  const eligible = form.data.recordable;
  const replacing = form.data.replacesDeathId !== null;
  const underlyingMissing = !filled(underlying) && manner !== 'pending_investigation';
  const ready =
    online &&
    mayRecord &&
    eligible &&
    day !== null &&
    validTime(time) &&
    certifier !== null &&
    manner !== null &&
    !underlyingMissing &&
    !record.isPending;

  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={v.patientId} />
      <PageHeader
        title="Record death"
        description={`On visit ${v.visitNumber}. Only record a death you or the certifying clinician have confirmed.`}
      />
      {!mayRecord ? (
        <Notice tone="info">
          You don&apos;t have permission to record deaths. Ask the clinician who confirmed the
          death.
        </Notice>
      ) : !eligible ? (
        <Notice tone="info">
          A death is recorded from the patient&apos;s open stay or casualty visit. Open that visit
          and record the death there.
        </Notice>
      ) : (
        <Section title="Death">
          {replacing ? (
            <Notice tone="info">
              The death recorded on this stay was marked entered in error. Recording it again
              replaces that record. The stay stays discharged as died.
            </Notice>
          ) : null}
          {form.data.birthCheck ? (
            <Notice tone="info">
              No birth is recorded on this visit. If the patient gave birth during it, record the
              birth as well. It can still be recorded on this visit after the death.
            </Notice>
          ) : null}
          <form
            className={styles.form}
            aria-label="Record death"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready) setConfirming(true);
            }}
          >
            <div className={styles.row}>
              <DateField label="Date of death" value={day} onChange={setDay} />
              <TextField
                label="Time of death"
                description="24-hour, like 14:05"
                value={time}
                onChange={setTime}
                width="sm"
                {...(time !== '' && !validTime(time)
                  ? { errorMessage: 'Write the time as hours and minutes, like 14:05.' }
                  : {})}
              />
            </div>
            {v.type === 'inpatient' ? (
              <p className={styles.meta}>Place of death: {PLACE_WORDS.ward}</p>
            ) : (
              <RadioGroup
                label="Place of death"
                orientation="horizontal"
                value={chosenPlace}
                onChange={(p) => {
                  setPlace(p === 'brought_in_dead' ? 'brought_in_dead' : 'casualty');
                }}
                options={[
                  { id: 'casualty', label: PLACE_WORDS.casualty },
                  { id: 'brought_in_dead', label: PLACE_WORDS.brought_in_dead },
                ]}
              />
            )}
            <Select
              label="Certifying clinician"
              placeholder="Choose one"
              options={(options.data?.certifiers ?? []).map((c) => ({
                id: c.id,
                label: c.displayName,
              }))}
              value={certifier}
              onChange={(id) => {
                setCertifier(typeof id === 'string' ? id : null);
              }}
            />
            <CauseFields cause={immediate} onChange={setImmediate} />
            <CauseFields cause={underlying} onChange={setUnderlying} />
            {contributing.map((c) => (
              <CauseFields
                key={c.key}
                cause={c}
                onChange={(next) => {
                  setContributing((all) => all.map((x) => (x.key === c.key ? next : x)));
                }}
                onRemove={() => {
                  setContributing((all) => all.filter((x) => x.key !== c.key));
                }}
              />
            ))}
            <div className={styles.actions}>
              <Button
                variant="secondary"
                onPress={() => {
                  setContributing((all) => [...all, emptyCause('contributing')]);
                }}
              >
                Add contributing condition
              </Button>
            </div>
            <RadioGroup
              label="Manner of death"
              description="Provisional list until the official form is confirmed."
              value={manner ?? ''}
              onChange={(m) => {
                setManner(m as DeathManner);
              }}
              options={DEATH_MANNERS.map((m) => ({ id: m, label: MANNER_WORDS[m] }))}
            />
            {underlyingMissing && manner !== null ? (
              <Notice tone="info">
                Add the underlying cause, or choose &ldquo;Pending investigation&rdquo; as the
                manner.
              </Notice>
            ) : null}
            <TextArea label="Notes (optional)" value={notes} onChange={setNotes} rows={3} />
            {!online ? (
              <Notice tone="info">
                No connection to Wodi. Recording a death waits for the connection; nothing is saved
                on this device.
              </Notice>
            ) : null}
            <div className={styles.actions}>
              <Button type="submit" variant="primary" isDisabled={!ready}>
                Record death
              </Button>
            </div>
          </form>
        </Section>
      )}
      <Dialog
        title="Record this death?"
        isOpen={confirming}
        onOpenChange={(open) => {
          if (!open) setConfirming(false);
        }}
      >
        <p className={styles.prose}>
          {replacing
            ? 'The patient will be shown as deceased everywhere again, with this record in place of the one entered in error.'
            : v.type === 'inpatient'
              ? 'The patient will be shown as deceased everywhere. Their stay is discharged as died at the time of death, the bed goes for cleaning, and their medicine orders stop.'
              : 'The patient will be shown as deceased everywhere, and their medicine orders on this visit stop.'}{' '}
          New visits, appointments and SMS reminders are stopped. A mistake can only be undone by a
          second person approving a correction.
        </p>
        {record.error === null ? null : (
          <Notice tone="error">
            {errorText(record.error, "The death wasn't recorded. Try again.")}
          </Notice>
        )}
        <DialogActions>
          <Button
            variant="quiet"
            onPress={() => {
              setConfirming(false);
            }}
          >
            Go back
          </Button>
          <Button
            variant="primary"
            isPending={record.isPending}
            pendingLabel="Recording…"
            onPress={() => {
              record.mutate();
            }}
          >
            Record death
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
